import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Warning,
  Assignment,
  Search,
  CheckCircle,
  Lock,
  TrendingUp,
  Timer,
  Speed,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

interface DashboardMetrics {
  total: number;
  open: number;
  assigned: number;
  investigating: number;
  resolved: number;
  closed: number;
  sla_breaches: number;
  avg_resolution_time_hours: number;
  sla_compliance_rate: number;
}

const IncidentDashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: metrics, isLoading, error } = useQuery<DashboardMetrics>({
    queryKey: ['incidentDashboard'],
    queryFn: async () => {
      const response = await axios.get('/api/incidents/dashboard');
      return response.data.data;
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load dashboard metrics. Please try again.
      </Alert>
    );
  }

  const MetricCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    subtitle,
    onClick 
  }: { 
    title: string; 
    value: number | string; 
    icon: React.ReactNode; 
    color: string;
    subtitle?: string;
    onClick?: () => void;
  }) => (
    <Card 
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: 3 } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}15`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Incident Management Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage security incidents across your organization
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Status Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Open Incidents"
            value={metrics?.open || 0}
            icon={<Warning sx={{ fontSize: 32, color: '#f44336' }} />}
            color="#f44336"
            onClick={() => navigate('/incidents?status=open')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Assigned"
            value={metrics?.assigned || 0}
            icon={<Assignment sx={{ fontSize: 32, color: '#ff9800' }} />}
            color="#ff9800"
            onClick={() => navigate('/incidents?status=assigned')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Investigating"
            value={metrics?.investigating || 0}
            icon={<Search sx={{ fontSize: 32, color: '#2196f3' }} />}
            color="#2196f3"
            onClick={() => navigate('/incidents?status=investigating')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Resolved"
            value={metrics?.resolved || 0}
            icon={<CheckCircle sx={{ fontSize: 32, color: '#4caf50' }} />}
            color="#4caf50"
            onClick={() => navigate('/incidents?status=resolved')}
          />
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Total Incidents"
            value={metrics?.total || 0}
            icon={<TrendingUp sx={{ fontSize: 32, color: '#9c27b0' }} />}
            color="#9c27b0"
            subtitle="All time"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="Avg Resolution Time"
            value={`${metrics?.avg_resolution_time_hours || 0}h`}
            icon={<Timer sx={{ fontSize: 32, color: '#00bcd4' }} />}
            color="#00bcd4"
            subtitle="Time to resolve"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <MetricCard
            title="SLA Compliance"
            value={`${metrics?.sla_compliance_rate || 0}%`}
            icon={<Speed sx={{ fontSize: 32, color: '#4caf50' }} />}
            color="#4caf50"
            subtitle={`${metrics?.sla_breaches || 0} breaches`}
          />
        </Grid>

        {/* SLA Breaches Alert */}
        {metrics && metrics.sla_breaches > 0 && (
          <Grid item xs={12}>
            <Alert 
              severity="warning"
              icon={<Warning />}
              action={
                <Chip 
                  label="View Breaches" 
                  size="small" 
                  onClick={() => navigate('/incidents?sla_breach=true')}
                  sx={{ cursor: 'pointer' }}
                />
              }
            >
              <Typography variant="body2">
                <strong>{metrics.sla_breaches}</strong> incident{metrics.sla_breaches !== 1 ? 's have' : ' has'} breached SLA deadlines and require immediate attention.
              </Typography>
            </Alert>
          </Grid>
        )}

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip
                  label="View All Incidents"
                  icon={<TrendingUp />}
                  onClick={() => navigate('/incidents')}
                  clickable
                  color="primary"
                />
                <Chip
                  label="My Assigned Incidents"
                  icon={<Assignment />}
                  onClick={() => navigate('/incidents?assigned_to_me=true')}
                  clickable
                  color="secondary"
                />
                <Chip
                  label="Critical Incidents"
                  icon={<Warning />}
                  onClick={() => navigate('/incidents?severity=critical')}
                  clickable
                  color="error"
                />
                <Chip
                  label="SLA Breaches"
                  icon={<Timer />}
                  onClick={() => navigate('/incidents?sla_breach=true')}
                  clickable
                  color="warning"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default IncidentDashboard;
